import React, { useState, useEffect } from 'react';
import { useToast } from '../../../components/Toast';
import './EducationProgramHeroForm.css';

const MAX_IMAGE_SIZE = 5 * 1024 * 1024;
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

export default function StudentLifeClubsForm({ apiBase }) {
  const toastUtil = useToast();
  const ToastContainer = toastUtil.ToastContainer;
  const toast = toastUtil;

  // Form values (auto-filled from content_published)
  const [formData, setFormData] = useState({
    kicker: '',
    title: '',
    description: '',
    clubs: []
  });

  // Pending approvals per field (kicker, title, description)
  const [pendingFields, setPendingFields] = useState({
    kicker: false,
    title: false,
    description: false
  });

  // Pending approvals per club (club_0, club_1, etc)
  const [pendingClubs, setPendingClubs] = useState({});

  // Track original values to detect changes
  const [originalData, setOriginalData] = useState({...formData});

  const [loading, setLoading] = useState(true);
  const [sectionId, setSectionId] = useState(null);
  const [submitting, setSubmitting] = useState({});
  const [imageUploading, setImageUploading] = useState({});
  const [imageProgress, setImageProgress] = useState({});
  const [editingClubIndex, setEditingClubIndex] = useState(null);
  const [deletingClubIndex, setDeletingClubIndex] = useState(null);
  const [mediaMap, setMediaMap] = useState({});

  useEffect(() => {
    // Load media map and section data in parallel
    const loadData = async () => {
      await Promise.all([loadMediaMap(), loadSection()]);
    };
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase]);

  const loadMediaMap = async () => {
    try {
      console.log('[StudentLifeClubsForm] Fetching media map...');
      const response = await fetch(
        `${apiBase}/studentlife/cms/media?_t=${Date.now()}`,
        { 
          credentials: 'include',
          headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' }
        }
      );
      
      if (!response.ok) {
        console.warn('[StudentLifeClubsForm] Media fetch failed:', response.status);
        return;
      }
      
      const result = await response.json();
      
      if (result.ok && Array.isArray(result.data)) {
        const map = {};
        result.data.forEach((media) => {
          const mediaId = media.id || media.media_id;
          const mediaUrl = media.url || media.file_path || media.filepath || '';
          
          if (mediaId && mediaUrl) {
            map[mediaId] = mediaUrl;
            map[String(mediaId)] = mediaUrl;
            map[parseInt(mediaId)] = mediaUrl;
            console.log(`[StudentLifeClubsForm] ✓ Media ${mediaId} → ${mediaUrl.substring(0, 60)}...`);
          }
        });
        
        console.log('[StudentLifeClubsForm] ✓ Media map ready:', Object.keys(map).length, 'items');
        setMediaMap(map);
      }
    } catch (err) {
      console.error('[StudentLifeClubsForm] Media fetch error:', err.message);
    }
  };

  const loadSection = async () => {
    setLoading(true);
    try {
      console.log('[StudentLifeClubsForm] Loading student life clubs section...');
      
      // Approach 1: Try admin endpoint (requires auth)
      console.log('[StudentLifeClubsForm] Trying authenticated endpoint...');
      const adminResponse = await fetch(
        `${apiBase}/studentlife/cms/sections?_t=${Date.now()}`,
        { 
          credentials: 'include',
          headers: { 
            'Content-Type': 'application/json',
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache'
          }
        }
      );
      
      let clubsSection = null;
      
      if (adminResponse.ok) {
        const result = await adminResponse.json();
        
        if (result.ok && Array.isArray(result.data)) {
          clubsSection = result.data.find(s => s.section_type === 'student-life-clubs');
          console.log('[StudentLifeClubsForm] ✓ Got data from admin endpoint');
        }
      } else if (adminResponse.status === 401) {
        console.log('[StudentLifeClubsForm] Admin endpoint unauthorized, trying public endpoint...');
      }
      
      // Approach 2: Fallback to public endpoint
      if (!clubsSection) {
        const publicResponse = await fetch(
          `${apiBase}/studentlife/cms/sections?published=true&_t=${Date.now()}`,
          { 
            credentials: 'include',
            headers: { 
              'Content-Type': 'application/json',
              'Cache-Control': 'no-cache, no-store, must-revalidate',
              'Pragma': 'no-cache'
            }
          }
        );
        
        if (publicResponse.ok) {
          const result = await publicResponse.json();
          
          if (result.ok && Array.isArray(result.data)) {
            clubsSection = result.data.find(s => s.section_type === 'student-life-clubs');
            console.log('[StudentLifeClubsForm] ✓ Got data from public endpoint');
          }
        } else {
          throw new Error(`Public endpoint returned ${publicResponse.status}`);
        }
      }
      
      if (!clubsSection) {
        throw new Error('Clubs section not found');
      }

      const sectionId = clubsSection.id;
      setSectionId(sectionId);

      console.log('[StudentLifeClubsForm] Found clubs section:', sectionId);

      // Load section with pending changes (if authenticated)
      let pendingFieldsMap = { kicker: false, title: false, description: false };
      let pendingClubsMap = {};
      
      const pendingResponse = await fetch(
        `${apiBase}/cms/section/${sectionId}/with-pending`,
        { 
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' }
        }
      );
      
      if (pendingResponse.ok) {
        try {
          const pendingResult = await pendingResponse.json();
          
          if (pendingResult.success && pendingResult.data && pendingResult.data.pending_changes) {
            const { pending_changes } = pendingResult.data;

            if (Array.isArray(pending_changes)) {
              pending_changes.forEach(change => {
                if (change.status === 'pending' && change.locale === 'en') {
                  if (change.field_id === 'kicker' || change.field_id === 'title' || change.field_id === 'description') {
                    pendingFieldsMap[change.field_id] = true;
                  } else if (change.field_id && change.field_id.includes('clubs.')) {
                    const match = change.field_id.match(/clubs\.(\d+)\./);
                    if (match) {
                      const clubIdx = parseInt(match[1]);
                      pendingClubsMap[`club_${clubIdx}`] = true;
                    }
                  }
                }
              });
            }
          }
        } catch (e) {
          console.warn('[StudentLifeClubsForm] Error parsing pending changes:', e);
        }
      }

      // Extract published content
      if (clubsSection.content_published) {
        let published = clubsSection.content_published;
        
        if (typeof published === 'string') {
          try {
            published = JSON.parse(published);
          } catch (e) {
            published = {};
          }
        }

        const publishedEn = published.en || {};
        
        const initialData = {
          kicker: publishedEn.kicker || '',
          title: publishedEn.title || '',
          description: publishedEn.description || '',
          clubs: Array.isArray(publishedEn.clubs) ? publishedEn.clubs : []
        };

        console.log(`[StudentLifeClubsForm] Initial data loaded:`, {
          kicker: initialData.kicker,
          title: initialData.title,
          description: initialData.description?.substring(0, 80),
          clubsCount: initialData.clubs.length
        });

        setFormData(initialData);
        setOriginalData(initialData);
        setPendingFields(pendingFieldsMap);
        setPendingClubs(pendingClubsMap);

        console.log('[StudentLifeClubsForm] ✓ Section loaded:', {
          id: sectionId,
          clubsCount: initialData.clubs.length,
          pendingFields: pendingFieldsMap,
          pendingClubs: pendingClubsMap
        });
      }
    } catch (err) {
      console.error('[StudentLifeClubsForm] Load error:', err);
      toast.error('Error loading form: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (fieldName, value) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };

  const handleClubChange = (index, field, value) => {
    setFormData(prev => {
      const newClubs = [...prev.clubs];
      if (typeof newClubs[index] !== 'object' || newClubs[index] === null) {
        newClubs[index] = {};
      }
      newClubs[index] = {
        ...newClubs[index],
        [field]: value
      };
      return {
        ...prev,
        clubs: newClubs
      };
    });
  };

  const handleImageUpload = async (clubIndex, imgIndex, e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > MAX_IMAGE_SIZE) {
      toast.error(`Max 5MB, got ${(file.size / 1024 / 1024).toFixed(2)}MB`);
      return;
    }

    if (!ALLOWED_TYPES.includes(file.type)) {
      toast.error('Only JPG, PNG, WebP allowed');
      return;
    }

    const key = `${clubIndex}-${imgIndex}`;
    setImageUploading(prev => ({ ...prev, [key]: true }));
    setImageProgress(prev => ({ ...prev, [key]: 0 }));

    try {
      const formDataToUpload = new FormData();
      formDataToUpload.append('file', file);

      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const percent = Math.round((e.loaded / e.total) * 100);
          console.log('[StudentLifeClubsForm] Upload progress:', percent + '%');
          setImageProgress(prev => ({ ...prev, [key]: percent }));
        }
      });

      xhr.addEventListener('load', () => {
        console.log('[StudentLifeClubsForm] Upload completed, status:', xhr.status);
        console.log('[StudentLifeClubsForm] Response text:', xhr.responseText);
        
        if (xhr.status === 200 || xhr.status === 201) {
          try {
            const response = JSON.parse(xhr.responseText);
            console.log('[StudentLifeClubsForm] Parsed response:', response);
            
            if (response.success || response.ok) {
              const mediaId = response.media_id || response.id || response.filename;
              console.log('[StudentLifeClubsForm] ✓ Image uploaded, media ID:', mediaId);
              
              if (mediaId) {
                handleClubChange(clubIndex, 'images', 
                  Array.isArray(formData.clubs[clubIndex]?.images) 
                    ? formData.clubs[clubIndex].images.map((img, i) => i === imgIndex ? mediaId : img)
                    : [mediaId]
                );
                toast.success('✓ Image uploaded (ID: ' + mediaId + ')');
              } else {
                toast.error('No media ID in response');
              }
            } else {
              toast.error(response.error || 'Upload failed');
            }
          } catch (parseErr) {
            console.error('[StudentLifeClubsForm] JSON parse error:', parseErr);
            toast.error('Invalid server response');
          }
        } else {
          console.error('[StudentLifeClubsForm] Upload failed with status', xhr.status);
          toast.error('Upload failed (HTTP ' + xhr.status + ')');
        }
        setImageUploading(prev => ({ ...prev, [key]: false }));
      });

      xhr.addEventListener('error', () => {
        console.error('[StudentLifeClubsForm] XHR error event');
        toast.error('Upload failed - network error');
        setImageUploading(prev => ({ ...prev, [key]: false }));
      });

      console.log('[StudentLifeClubsForm] Opening XHR to:', `${apiBase}/studentlife/cms/media/upload`);
      xhr.open('POST', `${apiBase}/studentlife/cms/media/upload`);
      xhr.withCredentials = true;
      xhr.send(formDataToUpload);
    } catch (err) {
      console.error('[StudentLifeClubsForm] Upload error:', err);
      toast.error('Upload error: ' + err.message);
      setImageUploading(prev => ({ ...prev, [key]: false }));
    }
  };

  const handleSubmitField = async (fieldName) => {
    if (!sectionId) {
      toast.error('Section ID not loaded');
      return;
    }

    // Check if value changed
    if (JSON.stringify(formData[fieldName]) === JSON.stringify(originalData[fieldName])) {
      toast.warning('No changes to submit');
      return;
    }

    console.log(`[StudentLifeClubsForm] Submitting field: ${fieldName}`, {
      old: originalData[fieldName],
      new: formData[fieldName],
      sectionId
    });

    setSubmitting(prev => ({ ...prev, [fieldName]: true }));

    try {
      const requestBody = {
        section_id: sectionId,
        field_id: fieldName,
        field_name: fieldName.replace(/_/g, ' '),
        new_value: formData[fieldName],
        old_value: originalData[fieldName],
        locale: 'en'
      };
      
      console.log(`[StudentLifeClubsForm] Sending request:`, JSON.stringify(requestBody));

      const response = await fetch(
        `${apiBase}/cms/field-change/submit`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(requestBody)
        }
      );

      const result = await response.json();
      console.log(`[StudentLifeClubsForm] Response from submit:`, result);

      if (result.success) {
        toast.success(`Submitted for approval`);
        
        // Mark as pending locally
        setPendingFields(prev => ({
          ...prev,
          [fieldName]: true
        }));

        // Update original data
        setOriginalData(prev => ({
          ...prev,
          [fieldName]: formData[fieldName]
        }));
      } else {
        if (response.status === 409) {
          toast.error('This field already has a pending approval');
        } else {
          toast.error(result.error || result.message || 'Submit failed');
        }
      }
    } catch (err) {
      toast.error('Error: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [fieldName]: false }));
    }
  };

  const handleSubmitClub = async (clubIndex, operation = null) => {
    if (!sectionId) {
      toast.error('Section ID not loaded');
      return;
    }

    const club = formData.clubs[clubIndex];
    if (!club) {
      toast.error('Club not found');
      return;
    }

    // Validate club has title (not needed for delete)
    if (operation !== 'delete' && (!club.title || club.title.trim() === '')) {
      toast.error('Club title is required');
      return;
    }

    // Auto-detect operation if not provided
    if (!operation) {
      // isNewClub: club exists in formData but NOT in originalData
      const isNewClub = !originalData.clubs[clubIndex];
      operation = isNewClub ? 'add' : 'update';
    }

    const clubKey = `club_${clubIndex}`;
    setSubmitting(prev => ({ ...prev, [clubKey]: true }));

    try {
      // Ensure images are array of IDs (strings or numbers)
      const clubToSubmit = operation === 'delete' ? null : {
        ...club,
        highlights: Array.isArray(club.highlights) ? club.highlights : [],
        images: Array.isArray(club.images) ? club.images.filter(Boolean) : []
      };

      const fieldId = `clubs.${clubIndex}.${operation}`;
      const fieldName = operation === 'add' ? `Add Club: ${club.title}` :
                        operation === 'update' ? `Update Club: ${club.title}` :
                        `Delete Club: ${club.title}`;

      console.log('[StudentLifeClubsForm] Submitting club operation:', {
        clubIndex, operation, fieldId,
        isNew: !originalData.clubs[clubIndex],
        sectionId
      });

      const response = await fetch(
        `${apiBase}/cms/field-change/submit`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            section_id: sectionId,
            field_id: fieldId,
            field_name: fieldName,
            old_value: originalData.clubs[clubIndex] || null,
            new_value: clubToSubmit,
            locale: 'en',
            operation_type: operation
          })
        }
      );

      const result = await response.json();
      console.log('[StudentLifeClubsForm] Submit response:', result);

      if (result.success) {
        const opLabel = operation === 'add' ? 'added' : operation === 'update' ? 'updated' : 'deleted';
        toast.success(`Club ${opLabel} and submitted for approval`);

        if (operation === 'add') {
          // Mark pending for this new club index
          setPendingClubs(prev => ({ ...prev, [clubKey]: true }));
          // Update originalData to include this new club so subsequent edits use 'update'
          setOriginalData(prev => ({
            ...prev,
            clubs: [...prev.clubs, clubToSubmit]
          }));
        } else if (operation === 'update') {
          setPendingClubs(prev => ({ ...prev, [clubKey]: true }));
          setOriginalData(prev => ({
            ...prev,
            clubs: prev.clubs.map((c, i) => i === clubIndex ? clubToSubmit : c)
          }));
        } else if (operation === 'delete') {
          // Remove from both formData and originalData locally
          setFormData(prev => ({
            ...prev,
            clubs: prev.clubs.filter((_, i) => i !== clubIndex)
          }));
          setOriginalData(prev => ({
            ...prev,
            clubs: prev.clubs.filter((_, i) => i !== clubIndex)
          }));
          setPendingClubs(prev => {
            const next = { ...prev };
            delete next[clubKey];
            return next;
          });
        }

        setEditingClubIndex(null);
        setDeletingClubIndex(null);
      } else {
        if (response.status === 409) {
          toast.error(`Club already has a pending approval`);
        } else {
          toast.error(result.error || result.message || 'Submit failed');
        }
      }
    } catch (err) {
      console.error('[StudentLifeClubsForm] Submit error:', err);
      toast.error('Error: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [clubKey]: false }));
    }
  };

  if (loading) {
    return (
      <div className="education-hero-form-container">
        <div className="loading">
          <div className="loading-spinner"></div>
          Loading Clubs & ECA Section...
        </div>
      </div>
    );
  }

  return (
    <div className="education-hero-form-container">
      <ToastContainer />

      <div className="hero-form-wrapper">
        <div className="hero-form-header">
          <h2 className="hero-form-title">Clubs & Extracurricular Activities</h2>
          <p className="hero-form-description">Manage clubs section header, content, and individual club details</p>
        </div>

        {/* KICKER SECTION */}
        <div className="form-section">
          <h3 className="form-section-title">Section Kicker</h3>
          <div className="form-field-card">
            <div className="field-header">
              <label>Kicker *</label>
              {pendingFields.kicker && (
                <span className="status-badge pending">⏳ Pending</span>
              )}
            </div>
            <input
              type="text"
              maxLength="100"
              value={formData.kicker}
              onChange={(e) => handleFieldChange('kicker', e.target.value)}
              disabled={submitting.kicker || pendingFields.kicker}
              placeholder="e.g., Clubs & ECA"
              className="text-input"
            />
            <button
              onClick={() => handleSubmitField('kicker')}
              disabled={submitting.kicker || pendingFields.kicker || formData.kicker === originalData.kicker}
              className="submit-btn"
            >
              {submitting.kicker ? 'Submitting...' : 'Submit for Approval'}
            </button>
          </div>
        </div>

        {/* TITLE SECTION */}
        <div className="form-section">
          <h3 className="form-section-title">Section Title</h3>
          <div className="form-field-card">
            <div className="field-header">
              <label>Title *</label>
              {pendingFields.title && (
                <span className="status-badge pending">⏳ Pending</span>
              )}
            </div>
            <input
              type="text"
              maxLength="200"
              value={formData.title}
              onChange={(e) => handleFieldChange('title', e.target.value)}
              disabled={submitting.title || pendingFields.title}
              placeholder="e.g., Explore activities that build confidence..."
              className="text-input"
            />
            <button
              onClick={() => handleSubmitField('title')}
              disabled={submitting.title || pendingFields.title || formData.title === originalData.title}
              className="submit-btn"
            >
              {submitting.title ? 'Submitting...' : 'Submit for Approval'}
            </button>
          </div>
        </div>

        {/* DESCRIPTION SECTION */}
        <div className="form-section">
          <h3 className="form-section-title">Section Description</h3>
          <div className="form-field-card">
            <div className="field-header">
              <label>Description *</label>
              {pendingFields.description && (
                <span className="status-badge pending">⏳ Pending</span>
              )}
            </div>
            <textarea
              maxLength="500"
              value={formData.description}
              onChange={(e) => handleFieldChange('description', e.target.value)}
              disabled={submitting.description || pendingFields.description}
              placeholder="Brief description of clubs and extracurricular activities..."
              className="textarea-input"
              rows="4"
            />
            <button
              onClick={() => handleSubmitField('description')}
              disabled={submitting.description || pendingFields.description || formData.description === originalData.description}
              className="submit-btn"
            >
              {submitting.description ? 'Submitting...' : 'Submit for Approval'}
            </button>
          </div>
        </div>

        {/* CLUBS SECTION */}
        <div className="form-section">
          <h3 className="form-section-title">Clubs ({formData.clubs.length})</h3>

          <div className="cards-grid">
            {formData.clubs.length === 0 ? (
              <div className="items-empty-state" style={{ gridColumn: '1 / -1' }}>
                <div className="empty-icon">🎯</div>
                <h4>No clubs configured</h4>
                <p>Click "Add New Club" to create your first club</p>
              </div>
            ) : (
              formData.clubs.map((club, idx) => {
                let clubImage = '';
                if (typeof club === 'object' && club !== null && club.images) {
                  clubImage = club.images[0] || '';
                }
                
                return (
                  <div
                    key={idx}
                    className="slide-card"
                    onClick={() => setEditingClubIndex(idx)}
                  >
                    {clubImage && mediaMap[clubImage] && (
                      <div className="card-image">
                        <img src={mediaMap[clubImage]} alt={club.title || `Club ${idx + 1}`} />
                      </div>
                    )}
                    <div className="card-content">
                      <div className="card-order">Club {idx + 1}</div>
                      <h4 className="card-title">{club.title || 'Club'}</h4>
                      <p className="card-desc">{club.subtitle || 'Click to edit'}</p>
                    </div>
                    {pendingClubs[`club_${idx}`] && (
                      <div className="card-pending">⏳</div>
                    )}
                  </div>
                );
              })
            )}

            {/* Add New Club Card */}
            <div
              className="slide-card add-card"
              onClick={() => {
                const newClub = {
                  title: '',
                  subtitle: '',
                  description: '',
                  images: [],
                  highlights: []
                };
                setFormData(prev => {
                  const updated = {
                    ...prev,
                    clubs: [...prev.clubs, newClub]
                  };
                  // Set editing to new club after state updates
                  setTimeout(() => setEditingClubIndex(updated.clubs.length - 1), 0);
                  return updated;
                });
              }}
              style={{ cursor: 'pointer', opacity: 0.7 }}
            >
              <div className="card-content" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '48px', marginBottom: '12px' }}>➕</div>
                  <h4 style={{ margin: 0, fontSize: '16px' }}>Add New Club</h4>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* EDIT CLUB MODAL */}
        {editingClubIndex !== null && formData.clubs[editingClubIndex] && (
          <div className="modal-overlay" onClick={() => setEditingClubIndex(null)}>
            <div className="modal-dialog" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Edit Club: {formData.clubs[editingClubIndex].title || `Club ${editingClubIndex + 1}`}</h3>
                <button className="modal-close" onClick={() => setEditingClubIndex(null)}>×</button>
              </div>

              <div className="modal-body">
                <div className="modal-field">
                  <label>Club Title</label>
                  <input
                    type="text"
                    value={formData.clubs[editingClubIndex].title || ''}
                    onChange={(e) => handleClubChange(editingClubIndex, 'title', e.target.value)}
                    placeholder="e.g., Sports Club"
                    className="text-input"
                  />
                </div>

                <div className="modal-field">
                  <label>Club Subtitle</label>
                  <input
                    type="text"
                    value={formData.clubs[editingClubIndex].subtitle || ''}
                    onChange={(e) => handleClubChange(editingClubIndex, 'subtitle', e.target.value)}
                    placeholder="e.g., Football, Basketball, Tennis"
                    className="text-input"
                  />
                </div>

                <div className="modal-field">
                  <label>Description</label>
                  <textarea
                    value={formData.clubs[editingClubIndex].description || ''}
                    onChange={(e) => handleClubChange(editingClubIndex, 'description', e.target.value)}
                    placeholder="Detailed description of the club..."
                    className="textarea-input"
                    rows="3"
                  />
                </div>

                <div className="modal-field">
                  <label>Highlights (Checklis Points)</label>
                  <div style={{ marginBottom: '12px' }}>
                    {(() => {
                      const highlights = formData.clubs[editingClubIndex]?.highlights;
                      const highlightsArray = Array.isArray(highlights) ? [...highlights] : [];
                      // Ensure at least 3 highlight slots
                      while (highlightsArray.length < 3) {
                        highlightsArray.push('');
                      }
                      return highlightsArray.map((highlight, hIdx) => (
                        <div key={hIdx} style={{ marginBottom: '10px', display: 'flex', gap: '8px', alignItems: 'center' }}>
                          <input
                            type="text"
                            value={highlight || ''}
                            onChange={(e) => {
                              const newHighlights = [...highlightsArray];
                              newHighlights[hIdx] = e.target.value;
                              handleClubChange(editingClubIndex, 'highlights', newHighlights.filter(Boolean));
                            }}
                            placeholder={`Point ${hIdx + 1}`}
                            className="text-input"
                            style={{ flex: 1 }}
                          />
                          <button
                            type="button"
                            onClick={() => {
                              const newHighlights = highlightsArray.filter((_, i) => i !== hIdx);
                              handleClubChange(editingClubIndex, 'highlights', newHighlights);
                            }}
                            style={{ background: '#dc3545', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer' }}
                          >
                            ×
                          </button>
                        </div>
                      ));
                    })()}
                    <button
                      type="button"
                      onClick={() => {
                        const current = formData.clubs[editingClubIndex]?.highlights || [];
                        handleClubChange(editingClubIndex, 'highlights', [...current, '']);
                      }}
                      style={{ background: '#007bff', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '4px', cursor: 'pointer', marginTop: '8px' }}
                    >
                      + Add Highlight
                    </button>
                  </div>
                  <span className="hint">Add key points for this club (with checkmark icon on homepage)</span>
                </div>

                <div className="modal-field">
                  <label>Club Images</label>
                  {(() => {
                    const images = formData.clubs[editingClubIndex]?.images;
                    const imageArray = Array.isArray(images) ? [...images] : [];
                    // Ensure at least 2 image slots for new clubs
                    while (imageArray.length < 2) {
                      imageArray.push(null);
                    }
                    return imageArray.map((imgId, imgIdx) => (
                      <div key={imgIdx} style={{marginBottom: '20px'}}>
                        {imgId && mediaMap[imgId] && (
                          <div className="modal-image-preview">
                            <img src={mediaMap[imgId]} alt={`Club Image ${imgIdx + 1}`} />
                          </div>
                        )}
                        <div className="image-upload-area">
                          <input
                            type="file"
                            accept="image/jpeg,image/png,image/webp"
                            onChange={(e) => handleImageUpload(editingClubIndex, imgIdx, e)}
                            disabled={imageUploading[`${editingClubIndex}-${imgIdx}`]}
                            className="image-input"
                            id={`club-image-${editingClubIndex}-${imgIdx}`}
                          />
                          <label htmlFor={`club-image-${editingClubIndex}-${imgIdx}`} className={`upload-label ${imageUploading[`${editingClubIndex}-${imgIdx}`] ? 'uploading' : ''}`}>
                            {imageUploading[`${editingClubIndex}-${imgIdx}`] ? 
                              `Uploading ${imageProgress[`${editingClubIndex}-${imgIdx}`]}%...` : 
                              'Upload Image'}
                          </label>
                        </div>
                        <span className="hint">Max 5MB • Recommended: 1200×600px • JPG, PNG, WebP</span>
                      </div>
                    ));
                  })()}
                </div>
              </div>

              <div className="modal-footer">
                <div style={{ display: 'flex', gap: '12px' }}>
                  <button
                    className="btn-cancel"
                    onClick={() => setDeletingClubIndex(editingClubIndex)}
                    style={{ background: '#dc3545', flex: 1 }}
                  >
                    Delete
                  </button>
                </div>
                <div style={{ display: 'flex', gap: '12px', marginLeft: 'auto' }}>
                  <button className="btn-cancel" onClick={() => setEditingClubIndex(null)}>Cancel</button>
                  <button
                    className="btn-submit"
                    disabled={submitting[`club_${editingClubIndex}`]}
                    onClick={() => handleSubmitClub(editingClubIndex)}
                  >
                    {submitting[`club_${editingClubIndex}`] ? 'Submitting...' : 'Save & Submit'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* DELETE CONFIRMATION MODAL */}
        {deletingClubIndex !== null && (
          <div className="modal-overlay" onClick={() => setDeletingClubIndex(null)}>
            <div className="modal-dialog" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Delete Club?</h3>
                <button className="modal-close" onClick={() => setDeletingClubIndex(null)}>×</button>
              </div>

              <div className="modal-body">
                <p>Are you sure you want to delete <strong>"{formData.clubs[deletingClubIndex]?.title || `Club ${deletingClubIndex + 1}`}"</strong>?</p>
                <p style={{ color: '#666', fontSize: '14px', marginTop: '12px' }}>This action cannot be undone.</p>
              </div>

              <div className="modal-footer">
                <button className="btn-cancel" onClick={() => setDeletingClubIndex(null)}>Cancel</button>
                <button
                  className="btn-submit"
                  onClick={() => {
                    if (window.confirm(`Delete club "${formData.clubs[deletingClubIndex]?.title || `Club ${deletingClubIndex + 1}`}"? This will be submitted for approval.`)) {
                      handleSubmitClub(deletingClubIndex, 'delete');
                    }
                  }}
                  style={{ background: '#dc3545' }}
                  disabled={submitting[`club_${deletingClubIndex}`]}
                >
                  {submitting[`club_${deletingClubIndex}`] ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
